import { ReactNode } from 'react';
import type { createTranslator, Messages } from 'next-intl';
import type { LatLngLiteral } from 'leaflet';
import { permissionsList } from './constants';

export type PolygonShape = {
  type: 'Polygon';
  /** Outer ring. */
  coordinates: LatLngLiteral[];
  /** Interior rings (holes) cut out of the outer ring. */
  holes?: LatLngLiteral[][];
};
/** One plot made of several disjoint polygons (a KML MultiGeometry placemark). */
export type MultiPolygonShape = {
  type: 'MultiPolygon';
  /** polygons -> rings (outer ring first, then holes) -> points */
  coordinates: LatLngLiteral[][][];
};
export type CircleShape = {
  type: 'Circle';
  center: LatLngLiteral;
  radius: number; // meters
};
export type Shape = PolygonShape | CircleShape | MultiPolygonShape;
export interface NameIdPair {
  id: number;
  name: string;
}
export interface FullName {
  firstName: string;
  lastName: string;
}
export interface DescribedItem extends NameIdPair {
  description: string;
}
export interface ImagedItem extends NameIdPair {
  image: string;
}
export interface IPropertyCrop extends ImagedItem {
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  isPropertyCrop: true;
}

export interface ICrop extends ImagedItem {
  isPropertyCrop?: boolean;
}

export interface PolyggonBoundary {
  type: 'Polygon';
  // array of rings; each ring = array of [lng, lat]
  coordinates: [number, number][][];
}

export interface PointBoundary {
  type: 'Point';
  // array of rings; each ring = array of [lng, lat]
  coordinates: [number, number];

  properties: { radius: number; geometryType: 'Circle' };
}

export interface MultiPolygonBoundary {
  type: 'MultiPolygon';
  // array of polygons; each polygon = array of rings; each ring = array of [lat, lng]
  coordinates: [number, number][][][];
}

export type Boundary = PointBoundary | PolyggonBoundary | MultiPolygonBoundary;

export interface ActionMenuListProps {
  id: string;
  list: ((setOpen: (o: boolean) => void) => ReactNode)[];
}
export interface PaginationRequestProps {
  currentPage: number;
  limit: number;
}
export interface PaginationProps extends PaginationRequestProps {
  totalItems: number;
  totalPages: number;
}
export type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
};

export type MutationApiRes = {
  data: { message: string };
  message: string;
  statusCode: number;
};

export interface ITimeZone {
  name: string;
  countries: string[];
  utcOffset: number;
  utcOffsetStr: string;
  dstOffset: number;
  dstOffsetStr: string;
  aliasOf: string | null;
}

// Account Properties (Farms)
export interface CreateAccountPropertyRequest {
  name: string;
  type: string; // e.g., "farm"
  country: string;
  state: string;
  city: string;
  totalArea: number;
  measurementSystem: string; // e.g., "metric"
  areaUnit: string; // e.g., "hectare"
  currency: string; // e.g., "USD"
  location: {
    type: 'Point';
    coordinates: [number, number];
  };
  registrationChannel: string; // e.g., "web"
}

export interface AccountPropertyResponse {
  id: number;
  name: string;
  type: string;
  country: string | null;
  state: string | null;
  city: string | null;
  totalArea: number | null;
  measurementSystem: string | null;
  areaUnit: string | null;
  currency: string | null;
  location: {
    type: string;
    coordinates: [number, number];
  };
  registrationChannel: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddPropertyResponse extends AccountPropertyResponse {
  accountId: number;
  createdById: number;
  hasAccess: boolean;
  isDeleted: boolean;
  lastUpdatedById: number;
  status: string;
  timeZone: string | null;
  users: [{ id: number; firstName: string; lastName: string }];
}

export interface SwitchPropertyRequest {
  propertyId: number;
  accountId: number;
}

export enum MeasurementSystem {
  Metric = 'metric',
  Imperial = 'imperial',
}

export type MeasurementSystemType = `${MeasurementSystem}`;

export interface ICreatePropertyBody {
  name: string;
  type: 'farm';
  country: string;
  state: string;
  city: string;
  measurementSystem: MeasurementSystem;
  location: {
    type: 'Point';
    coordinates: [number, number];
  };
  registrationChannel: 'web';
  userIds?: number[];
}

// ************************* TEAM ************************* //
export interface ITeamTypeResponse {
  teamTypes: NameIdPair[];
  total: number;
}
export interface ITeamOnboardingSchema {
  teamName: string | null;
  teamSize: string | null;
  teamType: NameIdPair | null;
  jobTitle: NameIdPair | null;
  invitedEmails: string[] | null;
}
export interface ITeamOnboardingBody {
  teamName: string;
  teamSize?: string;
  teamTypeId?: number;
  jobTitleId?: string;
  priceId?: string;
  emails?: { email: string }[];
}

// ************************* PERMISSIONS ************************* //
export type TFunction = ReturnType<typeof createTranslator<Messages>>;

export type PaginatedResponse<T, K extends string> = {
  [P in K | 'total']: P extends K ? T[] : number;
};

export type IUserPermission = (typeof permissionsList)[number];

// ************************ Pending Invitations **************************
export interface PendingInvitation {
  id: string;
  uuid: string;
  accountId: number;
  accountName: string;
  role: NameIdPair;
  inviter: FullName;
  expiresAt: string;
}
export type PendingInvitationsRes = PaginatedResponse<
  PendingInvitation,
  'invitations'
>;
